import "./style.css";
import { Button, message, Typography, Input, DatePicker, Modal, Tooltip, Tabs, Dropdown, Steps, AutoComplete, Card, Space, Divider, Progress, Badge } from "antd";
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { catcher } from "../../../firebase/util";
import axios from "axios";
import { useAtom } from "jotai";
import { consultantAtom, reportDataAtom, tableDataSourceAtom } from "../atom/atoms";
import { downloadFileFromStorage } from "../../../firebase/storage/download";
import { getCurrentDate, getCurrentTime, getCurrentYear } from "../../../helper/time";
import { calculateTotals } from "../../extras/testing/other/banner-carbondi";
import store from "../../../redux/store";
import { getAnalysis } from "../../extras/testing/workers/year";
import { designations, findCompanies, firstCompany, getCurrentUserDesignation } from "../../../helper/attribute";
import { fixDecimals, getUniqueProps, removeDuplicates } from "../../../helper/wodash";
import { getForms } from "../../../static/formRoutes";
import { saveAs } from "file-saver";
import CbfReportDownload from "../../extras/testing/CbfReportDownload";
import mergeAndDownloadPDF from "./helper";
import { findForm } from "../../../static/formRoutes";
import CommentsTable from "./CommentsTable";
import { useSelector } from "react-redux";
import { selectResults, selectSites } from "../../../redux/features/appSlice";
import Loading from "../../../components/loading";
import { QuestionOutlined, DownOutlined, UserOutlined, LogoutOutlined, CheckCircleOutlined, EditOutlined, DownloadOutlined, CommentOutlined, PlusOutlined, SettingOutlined, FileTextOutlined, CloudDownloadOutlined, RocketOutlined, BuildOutlined } from "@ant-design/icons";
import AutoCompleteModal from "./AutoCompleteModal";
import { updateUserClicks } from "../../../firebase/user/update";
import { getAllParamsFromLocation } from "../../../helper/loc";
import { getGHGTitle } from "../../../static/ghg.static";
import { arrayToEnglishString, removeLastLine } from "../../../helper/string";
import { get_consultants } from "../../../firebase/super_admins/get_consultants";
import SelectSearch from "../../../components/select/SearchSelect";
import { getCacheOptions, updateCacheOptions } from "../../../firebase/cache/create";
import Permissions from "../../super-admin/SuperAdminSectionPages/Permissions";
import Companies from "../../admin/companies";
import ResultFunctions from "./Functions";
import LockData from "./LockData";
import AnalysisConsultant from "./AnalysisConsultant";
import Result from "../../results";
import AnalysisResult from "./AnalysisResult";

const { TabPane } = Tabs;

function convertValuesToStrings(list) {
  var groupedByScope = {};
  var grandTotals = {};

  // Group objects by scope
  list.forEach(function (obj) {
    var scope = obj["Scope"];
    if (!groupedByScope[scope]) {
      groupedByScope[scope] = [];
    }
    groupedByScope[scope].push(obj);
  });

  var finalList = [];

  // Process each scope group
  for (var scope in groupedByScope) {
    if (groupedByScope.hasOwnProperty(scope)) {
      var scopeObjects = groupedByScope[scope];
      var scopeTotals = {};

      // Process each object within the scope group
      scopeObjects.forEach(function (obj) {
        var newObj = {};

        // Process each key in the object
        for (var key in obj) {
          if (obj.hasOwnProperty(key)) {
            var value = obj[key];

            // Treat "Scope" key first
            if (key === "Scope") {
              newObj[key] = String(value);
            } else {
              // Convert numeric values to 2 decimal places and calculate totals
              if (!isNaN(value) && value !== "") {
                value = parseFloat(value)?.toFixed(2);
                // Accumulate totals for numeric values within the scope
                if (!scopeTotals[key]) {
                  scopeTotals[key] = 0;
                }
                scopeTotals[key] += parseFloat(value);

                // Accumulate grand totals across all scopes
                if (!grandTotals[key]) {
                  grandTotals[key] = 0;
                }
                grandTotals[key] += parseFloat(value);
              }
              newObj[key] = String(value);
            }
          }
        }
        finalList.push(newObj);
      });

      // Add a summary object for the scope
      var summaryObj = { Scope: "Scope " + scope + " Total" };

      // Add totals or empty strings for non-numeric fields
      for (var key in scopeTotals) {
        if (scopeTotals.hasOwnProperty(key)) {
          summaryObj[key] = fixDecimals(scopeTotals[key], 2, true);
        }
      }

      // Ensure that non-numeric fields are represented by empty strings in the total object
      for (var objKey in scopeObjects[0]) {
        if (!scopeTotals.hasOwnProperty(objKey) && objKey !== "Scope") {
          summaryObj[objKey] = "";
        }
      }

      finalList.push(summaryObj);
    }
  }

  // Add a grand total at the end
  var grandTotalObj = { Scope: "Total (Scope 1, 2 and 3)" };

  // Populate grand total values or empty strings for non-numeric fields
  for (var key in grandTotals) {
    if (grandTotals.hasOwnProperty(key)) {
      grandTotalObj[key] = fixDecimals(grandTotals[key], 2, true);
    }
  }

  // Ensure that non-numeric fields are represented by empty strings in the grand total object
  for (var key in list[0]) {
    if (!grandTotals.hasOwnProperty(key) && key !== "Scope") {
      grandTotalObj[key] = "";
    }
  }

  finalList.push(grandTotalObj);

  return finalList;
}

function convertListToColumns(list) {
  return list.map((item) => {
    return {
      Data: item.data,
      "Source of data": item.source || "",
      "Verifier comments": item.verifier || "",
    };
  });
}

function singleLineString(str) {
  return str.replace(/\s+/g, " ").trim();
}

function combineChildrenghg(data, year) {
  let combinedChildrenghg = [];

  // Iterate through each item in dataSource
  data.dataSource.forEach((scope) => {
    if (scope.childrenghg && Array.isArray(scope.childrenghg)) {
      // Filter based on the year provided in the params
      const filteredGHG = scope.childrenghg.filter((item) => item[year] !== undefined);
      combinedChildrenghg = combinedChildrenghg.concat(filteredGHG);
    }
  });

  return combinedChildrenghg;
}

const fieldConfig = {
  date: { type: "text", info: "The date of the report (DD/MM/YYYY format)" },
  fullName: { type: "text", info: "Full name of user." },
  marketBasedEmission: { type: "text", info: "Market based emission value" },
  locationBasedEmission: { type: "text", info: "Location based emission value" },
  companyName: { type: "text", info: "Name of the company" },
  startDate: { type: "text", info: "Follow DD/MM/YYYY format" },
  endDate: { type: "text", info: "Follow DD/MM/YYYY format" },
  accuracyComments: { type: "autocomplete", info: "Comments on the accuracy of the report" },
  recommendations: { type: "autocomplete", info: "Write recommendations separated by a new line." },
  consultant_signature: { type: "text", info: "Consultant's signature URL (signature to be used in report)" },
  finalComments: { type: "autocomplete", info: "Sign off comments" },

  consultant_details: { type: "textarea", info: "Details about the consultant" },
  consultant_summary: { type: "textarea", info: "Consultant summary" },
  // reviewer
  reviewer_details_without_phone: { type: "textarea", info: "Reviewer details" },
  reviewer_summary: { type: "textarea", info: "Reviewer summary" },
  // authorizer
  authorizer_details_without_phone: { type: "textarea", info: "Authorizer details" },
  authorizer_summary: { type: "textarea", info: "Authorizer summary" },
};

const Verification = () => {
  const results = useSelector(selectResults);
  const [consultant, setConsultant] = useAtom(consultantAtom);
  const [tableDataSource, setTableDataSource] = useState(tableDataSourceAtom);
  const [calculatedCommentsTable, setCalculatedCommentsTable] = useState([]);
  const [reportData, setReportData] = useAtom(reportDataAtom);
  const [year, setYear] = useState(getCurrentYear()?.toString());
  const [useWtt, setUseWtt] = useState(true);
  const [reviewer, setReviewer] = useState();
  const [authorizer, setAuthorizer] = useState();
  const sites = useSelector(selectSites);
  const consultants = useSelector((state) => state.superAdminData.consultants);
  const [currentStep, setCurrentStep] = useState(0);
  const [reportTypeModalVisible, setReportTypeModalVisible] = useState(false);
  const [consultantsLoaded, setConsultantsLoaded] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(0);
  const [isDownloading, setIsDownloading] = useState(false);
  const [autocompleteOptions, setAutocompleteOptions] = useState({
    accuracyComments: [],
    recommendations: [],
    finalComments: []
  });
  const [isAddOptionModalVisible, setIsAddOptionModalVisible] = useState(false);
  const [newOptionValue, setNewOptionValue] = useState("");
  const [currentField, setCurrentField] = useState("");
  const [autoCompleteModalVisible, setAutoCompleteModalVisible] = useState(false);
  const [currentEditingFieldKey, setCurrentEditingFieldKey] = useState("");

  // Load consultants first
  useEffect(() => {
    const loadConsultants = async () => {
      try {
        await get_consultants();
        setConsultantsLoaded(true);
      } catch (error) {
        console.error("Error loading consultants:", error);
        message.error("Failed to load consultants");
      }
    };

    if (!consultants) {
      loadConsultants();
    } else {
      setConsultantsLoaded(true);
    }
  }, [consultants]);

  // Load autocomplete suggestions from cache
  useEffect(() => {
    const loadAutocompleteOptions = async () => {
      try {
        const companyName = firstCompany()?.title;
        if (!companyName) return;

        const [accuracyCache, recommendationsCache, finalCommentsCache] = await Promise.all([
          getCacheOptions(companyName, "_accuracy_comments"),
          getCacheOptions(companyName, "_recommendations"),
          getCacheOptions(companyName, "_final_comments")
        ]);

        setAutocompleteOptions({
          accuracyComments: accuracyCache ? Object.values(accuracyCache) : [],
          recommendations: recommendationsCache ? Object.values(recommendationsCache) : [],
          finalComments: finalCommentsCache ? Object.values(finalCommentsCache) : []
        });
      } catch (error) {
        console.error("Error loading autocomplete options:", error);
      }
    };

    loadAutocompleteOptions();
  }, []);

  // Helper function to get the last day of a month
  const getLastDayOfMonth = (month, year) => {
    // month is 1-indexed (1 = January, 12 = December)
    const date = new Date(year, month, 0); // Day 0 gets the last day of the previous month
    return date.getDate();
  };

  // Helper function to convert month name to number
  const getMonthNumber = (monthName) => {
    const months = {
      'january': 1, 'jan': 1,
      'february': 2, 'feb': 2,
      'march': 3, 'mar': 3,
      'april': 4, 'apr': 4,
      'may': 5,
      'june': 6, 'jun': 6,
      'july': 7, 'jul': 7,
      'august': 8, 'aug': 8,
      'september': 9, 'sep': 9,
      'october': 10, 'oct': 10,
      'november': 11, 'nov': 11,
      'december': 12, 'dec': 12
    };

    if (typeof monthName === 'number') return monthName;
    if (typeof monthName === 'string') {
      const num = parseInt(monthName);
      if (!isNaN(num)) return num;
      return months[monthName.toLowerCase()] || null;
    }
    return null;
  };

  // Helper function to format date based on reporting year starting month
  const getReportingDates = (reportingYear, reportingStartMonth) => {
    if (!reportingStartMonth) {
      // Fallback to January-December if no reporting start month is set
      return {
        startDate: "01/01/" + reportingYear,
        endDate: "31/12/" + reportingYear,
      };
    }

    const startMonth = getMonthNumber(reportingStartMonth);
    if (!startMonth) {
      // Fallback if month conversion fails
      return {
        startDate: "01/01/" + reportingYear,
        endDate: "31/12/" + reportingYear,
      };
    }

    const endMonth = startMonth === 1 ? 12 : startMonth - 1; // Previous month to the reporting start month

    // For start date: first day of reporting year starting month in the selected year
    const startYear = startMonth === 1 ? Number(reportingYear) : Number(reportingYear) - 1;

    // For end date: last day of the month before reporting start month in the next year
    const endYear = startMonth === 1 ? Number(reportingYear) : startYear + 1;

    const endLastDay = getLastDayOfMonth(endMonth, endYear);
    const formatMonth = (month) => month.toString().padStart(2, '0');

    return {
      startDate: `01/${formatMonth(startMonth)}/${startYear}`,
      endDate: `${endLastDay}/${formatMonth(endMonth)}/${endYear}`,
    };
  };

  const getReportData = async (consultant) => {
    const company = firstCompany();
    const options = {
      wtt: useWtt ? "Included" : "Excluded",
      type: "Location",
      reporting: "All",
      sites: getCurrentUserDesignation() !== designations.group_admin ? [findCompanies()[0]?.id] : findCompanies()?.map((c) => c.id),
    };

    const locationAnalysis = getAnalysis(store.getState().app.results, options);
    const marketAnalysis = getAnalysis(store.getState().app.results, {
      ...options,
      type: "Market",
    });

    const locGhg = combineChildrenghg(locationAnalysis, year);
    const mktGhg = combineChildrenghg(marketAnalysis, year);
    const locationTotals = calculateTotals(locationAnalysis.dataSource, year, true);
    const marketTotals = calculateTotals(marketAnalysis.dataSource, year, true);

    const emissionTable = [];
    locGhg.forEach((item, index) => {
      const { scope, name } = item;
      const mktItem = mktGhg.find((m) => m.scope === scope && m.name === name);
      emissionTable.push({
        Scope: scope,
        "Emission Source": getGHGTitle(name),
        "Location-Based (tCO₂e)": fixDecimals(item[year], 2, true),
        "Market-Based (tCO₂e)": fixDecimals(mktItem?.[year], 2, true),
      });
    });

    const findc = (fullName) => {
      const x = consultants?.find((c) => c.firstName + " " + c.lastName === fullName);
      return x;
    };

    const rysp = firstCompany()?.reporting_year_starting_month;
    const { startDate, endDate } = getReportingDates(year, rysp);
    console.log('Verification.js - Reporting dates:', {
      year,
      reportingStartMonth: rysp,
      startDate,
      endDate
    });

    return {
      date: getCurrentDate(),
      fullName: store.getState().user.doc.firstName + " " + store.getState().user.doc.lastName,
      firstName: store.getState().user.doc.firstName,
      marketBasedEmission: fixDecimals(marketTotals),
      locationBasedEmission: fixDecimals(locationTotals),
      companyName: findCompanies()?.[0]?.title,
      startDate,
      endDate,
      accuracyComments: "",
      recommendations: "",
      finalComments: "",
      // consultant
      consultantEmail: consultant.email,
      //--details
      consultant_details: consultant.details_for_template,
      consultant_details_inline: singleLineString(removeLastLine(consultant.details_for_template)),
      consultant_details_without_phone: removeLastLine(consultant.details_for_template),
      consultant_signature: await downloadFileFromStorage(consultant.signature, "s.png", true),
      consultant_summary: consultant.summary_for_template,
      // reviwer
      reviewer_details_without_phone: removeLastLine(findc(reviewer)?.details_for_template, 2),
      reviewer_summary: findc(reviewer)?.summary_for_template,

      //authorizer
      authorizer_details_without_phone: removeLastLine(findc(authorizer)?.details_for_template, 2),
      authorizer_summary: findc(authorizer)?.summary_for_template,

      formNames: getForms().map(form => form.title),
      formCommentsTable: [],
      year,
      // pro
      companyAbout: `${company.title} is ${company.about}`,
      emissionTable: convertValuesToStrings(emissionTable),
      companyCountries: arrayToEnglishString(removeDuplicates(sites.map((site) => site.country))),
    };
  };

  useEffect(() => {
    if (consultant && consultantsLoaded) {
      getReportData(consultant).then(setReportData);
    }
  }, [consultant, year, useWtt, reviewer, authorizer, consultantsLoaded]);

  useEffect(() => {
    catcher(async () => {
      const { auth_key } = getAllParamsFromLocation();
      if (auth_key) {
        const { data } = await axios.post(`${process.env.REACT_APP_BE}/api/user/login/consultant`, { direct_using_uid: auth_key });
        if (!data.consultant) return message.error("Error getting consultant information");
        setConsultant(data);
      }
    }, {});
  }, []);

  useEffect(() => {
    if (results) {
      const availableForms = getForms();
      setTableDataSource(availableForms.map((form) => ({
        data: form.title,
        formName: form.name,
        key: form.name
      })));
    }
  }, [results]);

  const handleDownloadReport = useCallback(async (type = 1) => {
    setReportTypeModalVisible(false);
    setIsDownloading(true);
    setDownloadProgress(0);
    
    // Simulate progress for better UX
    const progressInterval = setInterval(() => {
      setDownloadProgress(prev => {
        if (prev >= 90) {
          clearInterval(progressInterval);
          return prev;
        }
        return prev + Math.random() * 15;
      });
    }, 200);

    catcher(
      async () => {
        let inputFile;

        function removeLowercaseKeys(arr) {
          return arr.map(obj => {
            const newObj = {};
            for (const key in obj) {
              if (key[0] !== key[0].toLowerCase()) {
                newObj[key] = obj[key];
              }
            }
            return newObj;
          });
        }
        
        if (type == 1) {
          await updateUserClicks("download_pdf_report");
          inputFile = await downloadFileFromStorage("assets/verification_template.docx", "template.docx", true);
        } else if (type == 2) {
          inputFile = await downloadFileFromStorage("assets/verification_template_pro.docx", "template.docx", true);
        } else {
          inputFile = await downloadFileFromStorage("assets/verification_template_dc.docx", "template.docx", true);
        }
        
        setDownloadProgress(70);
        
        const dataToSend = {
          inputFile,
          context: {
            ...reportData,
            recommendations: reportData.recommendations.split("\n"),
            formCommentsTable: removeLowercaseKeys(calculatedCommentsTable),
          },
        };
        
        const result = await axios.post("https://testing-wasilislam.pythonanywhere.com/docx/template", dataToSend, { responseType: "blob" });
        
        setDownloadProgress(100);
        saveAs(result.data, `${findCompanies()?.[0]?.title} Verification Report.docx`);

        // Save autocomplete suggestions
        const companyName = firstCompany()?.title;
        if (companyName) {
          await Promise.all([
            updateCacheOptions(companyName, { [getCurrentTime()]: reportData.accuracyComments }, "_accuracy_comments"),
            updateCacheOptions(companyName, { [getCurrentTime()]: reportData.recommendations }, "_recommendations"),
            updateCacheOptions(companyName, { [getCurrentTime()]: reportData.finalComments }, "_final_comments")
          ]);
        }
        
        clearInterval(progressInterval);
        setTimeout(() => {
          setIsDownloading(false);
          setDownloadProgress(0);
        }, 1000);
      },
      { 
        loading_msg: "Generating your report...",
        onFinally: () => {
          clearInterval(progressInterval);
          setIsDownloading(false);
          setDownloadProgress(0);
        }
      }
    );
  }, [reportData, tableDataSource]);

  const handleChange = useCallback((key, value) => {
    setReportData((prevData) => ({ ...prevData, [key]: value }));
  }, [setReportData]);

  // Handle adding new autocomplete option
  const handleAddNewAutocompleteOption = useCallback(async () => {
    if (newOptionValue.trim() && currentField) {
      const companyName = firstCompany()?.title;
      if (!companyName) return;

      const cacheKey = `_${currentField.replace('Comments', '_comments')}`;
      const currentTime = getCurrentTime();

      // Update cache with new option
      await updateCacheOptions(companyName, { [currentTime]: newOptionValue.trim() }, cacheKey);

      // Update local state
      setAutocompleteOptions(prev => ({
        ...prev,
        [currentField]: [...prev[currentField], newOptionValue.trim()]
      }));

      // Close modal and reset
      setIsAddOptionModalVisible(false);
      setNewOptionValue("");
      setCurrentField("");
    }
  }, [newOptionValue, currentField]);

  // Show add option modal
  const showAddOptionModal = useCallback((fieldKey) => {
    setCurrentField(fieldKey);
    setIsAddOptionModalVisible(true);
  }, []);

  // Handle saving from autocomplete modal
  const handleAutoCompleteModalSave = useCallback((value) => {
    if (currentEditingFieldKey) {
      handleChange(currentEditingFieldKey, value);
    }
    setAutoCompleteModalVisible(false);
    setCurrentEditingFieldKey("");
  }, [currentEditingFieldKey, handleChange]);

  // Handle adding suggestion from autocomplete modal
  const handleAddSuggestionFromAutoCompleteModal = useCallback(async (suggestion, fieldKey) => {
    const companyName = firstCompany()?.title;
    if (!companyName) return;

    const cacheKey = `_${fieldKey.replace('Comments', '_comments')}`;
    const currentTime = getCurrentTime();

    // Update cache with new option
    await updateCacheOptions(companyName, { [currentTime]: suggestion }, cacheKey);

    // Update local state
    setAutocompleteOptions(prev => ({
      ...prev,
      [fieldKey]: [...prev[fieldKey], suggestion]
    }));
  }, []);

  // Handle removing suggestion from autocomplete modal
  const handleRemoveSuggestionFromAutoCompleteModal = useCallback(async (suggestion, fieldKey) => {
    // Update local state
    setAutocompleteOptions(prev => ({
      ...prev,
      [fieldKey]: prev[fieldKey].filter(s => s !== suggestion)
    }));
  }, []);

  const renderField = useCallback((key, fieldConfigItem) => {
    const label = key
      .replace(/([A-Z])/g, " $1")
      .replace(/^./, (str) => str.toUpperCase())
      .replaceAll("_", " ");
    const commonProps = { key, className: "report-data-field" };
    const isEmpty = !reportData[key] || (typeof reportData[key] === "string" && reportData[key].trim() === "");
    const inputProps = {
      value: reportData[key] || "",
      onChange: (e) => handleChange(key, e.target.value),
      size: "large",
      status: isEmpty ? "error" : undefined,
    };

    switch (fieldConfigItem.type) {
      case "text":
        return (
          <div {...commonProps} style={{ marginBottom: "24px" }}>
            <Typography.Text strong style={{ display: "block", marginBottom: "8px", color: isEmpty ? "#ff4d4f" : "inherit" }}>
              {label}
            </Typography.Text>
            <Input {...inputProps} placeholder={`Enter ${label.toLowerCase()}`} />
          </div>
        );
      case "textarea":
        return (
          <div {...commonProps} style={{ marginBottom: "24px" }}>
            <Typography.Text strong style={{ display: "block", marginBottom: "8px", color: isEmpty ? "#ff4d4f" : "inherit" }}>
              {label}
            </Typography.Text>
            <Input.TextArea
              autoSize={{ minRows: 3 }}
              {...inputProps}
              placeholder={`Enter ${label.toLowerCase()}`}
            />
          </div>
        );
      case "autocomplete":
        const openModal = () => {
          setCurrentEditingFieldKey(key);
          setAutoCompleteModalVisible(true);
        };

        return (
          <div {...commonProps} style={{ marginBottom: "24px" }}>
            <Typography.Text strong style={{ display: "block", marginBottom: "8px", color: isEmpty ? "#ff4d4f" : "inherit" }}>
              {label}
            </Typography.Text>
            <Input.TextArea
              value={reportData[key] || ""}
              autoSize={{ minRows: 3 }}
              size="large"
              status={isEmpty ? "error" : undefined}
              placeholder={`Click to open editor with suggestions...`}
              style={{ cursor: 'pointer' }}
              onClick={openModal}
              readOnly
            />
          </div>
        );
      case "date":
        return (
          <div {...commonProps} style={{ marginBottom: "24px" }}>
            <Typography.Text strong style={{ display: "block", marginBottom: "8px", color: isEmpty ? "#ff4d4f" : "inherit" }}>
              {label}
            </Typography.Text>
            <DatePicker
              value={reportData[key]}
              onChange={(date) => handleChange(key, date)}
              size="large"
              style={{ width: "100%" }}
            />
          </div>
        );
      default:
        return null;
    }
  }, [reportData, handleChange, autocompleteOptions]);

  const renderReportDataFields = useMemo(() =>
    Object.keys(reportData).map((key) => fieldConfig[key] && renderField(key, fieldConfig[key]))
    , [reportData, renderField]);

  const steps = useMemo(() => [
    {
      title: 'Configuration',
      description: 'Setup report parameters',
      icon: <SettingOutlined />,
    },
    {
      title: 'Data Review',
      description: 'Validate report content',
      icon: <EditOutlined />,
    },
    {
      title: 'Comments',
      description: 'Add verification notes',
      icon: <CommentOutlined />,
    },
    {
      title: 'Export',
      description: 'Generate final reports',
      icon: <CloudDownloadOutlined />,
    },
  ], []);

  const renderCurrentStep = useCallback(() => {
    switch (currentStep) {
      case 0:
        return (
          <Card style={{ maxWidth: "600px", margin: "0 auto", border: '1px solid #e1e5e9' }}>
            <div style={{ textAlign: "center", marginBottom: "32px" }}>
              <div style={{ fontSize: '32px', marginBottom: '16px' }}>⚙️</div>
              <Typography.Title level={3} style={{ marginBottom: "8px", color: '#1f2937' }}>
                Configuration
              </Typography.Title>
              <Typography.Text type="secondary">
                Set up your verification report parameters
              </Typography.Text>
            </div>

            <Space direction="vertical" size="large" style={{ width: "100%" }}>
              <SelectSearch
                bgless
                borderless
                title="Include WTT Emissions"
                value={useWtt ? "Yes" : "No"}
                setValue={(val) => setUseWtt(val === "Yes")}
                options={["Yes", "No"]}
              />

              <SelectSearch
                bgless
                borderless
                title="Reporting Year"
                value={year}
                setValue={setYear}
                options={[2019, 2020, 2021, 2022, 2023, 2024, 2025].map(y => y.toString())}
              />

              <SelectSearch
                bgless
                borderless
                title="Reviewer"
                value={reviewer}
                setValue={setReviewer}
                options={consultants?.map((c) => `${c.firstName} ${c.lastName}`)}
              />

              <SelectSearch
                bgless
                borderless
                title="Authorizer"
                value={authorizer}
                setValue={setAuthorizer}
                options={consultants?.map((c) => `${c.firstName} ${c.lastName}`)}
              />
            </Space>

            <div style={{ textAlign: "center", marginTop: "32px" }}>
              <Button
                type="primary"
                size="large"
                onClick={() => setCurrentStep(1)}
                style={{ minWidth: "200px" }}
                icon={<EditOutlined />}
              >
                Continue
              </Button>
            </div>
          </Card>
        );
      case 1:
        return (
          <div style={{ maxWidth: "800px", margin: "0 auto" }}>
            <div style={{ textAlign: "center", marginBottom: "32px" }}>
              <Typography.Title level={3} style={{ marginBottom: "8px", color: '#1f2937' }}>
                Data Review
              </Typography.Title>
              <Typography.Text type="secondary">
                Review and validate report data
              </Typography.Text>
            </div>

            <Card style={{ border: '2px solid #e1e5e9', overflowY:"auto" }}>
              {renderReportDataFields}
            </Card>

            <div style={{ textAlign: "center", marginTop: "32px" }}>
              <Space size="middle">
                <Button 
                  size="large" 
                  onClick={() => setCurrentStep(0)}
                  icon={<SettingOutlined />}
                >
                  Back
                </Button>
                <Button 
                  type="primary" 
                  size="large" 
                  onClick={() => setCurrentStep(2)}
                  icon={<CommentOutlined />}
                >
                  Continue
                </Button>
              </Space>
            </div>
          </div>
        );
      case 2:
        return (
          <div style={{ maxWidth: "100%", margin: 0, padding: 0 }}>
            <div style={{ textAlign: "center", marginBottom: "24px" }}>
              <div style={{ fontSize: '32px', marginBottom: '16px' }}>💬</div>
              <Typography.Title level={3} style={{ marginBottom: "8px", color: '#1f2937' }}>
                Comments
              </Typography.Title>
              <Typography.Text type="secondary">
                Add verification comments
              </Typography.Text>
            </div>

            <Card style={{ border: '1px solid #e1e5e9' }}>
              <CommentsTable
                dataSource={tableDataSource}
                setDataSource={setTableDataSource}
                year={year}
                startDate={reportData?.startDate}
                endDate={reportData?.endDate}
                onCalculatedDataChange={(data)=>{
                  setCalculatedCommentsTable(data);
                }}
              />
            </Card>

            <div style={{ textAlign: "center", marginTop: "24px" }}>
              <Space size="middle">
                <Button 
                  size="large" 
                  onClick={() => setCurrentStep(1)}
                  icon={<EditOutlined />}
                >
                  Back
                </Button>
                <Button 
                  type="primary" 
                  size="large" 
                  onClick={() => setCurrentStep(3)}
                  icon={<CloudDownloadOutlined />}
                >
                  Continue
                </Button>
              </Space>
            </div>
          </div>
        );
      case 3:
        return (
          <div style={{ maxWidth: "600px", margin: "0 auto" }}>
            <div style={{ textAlign: "center", marginBottom: "32px" }}>
              <Typography.Title level={3} style={{ marginBottom: "8px", color: '#1f2937' }}>
                Download Reports
              </Typography.Title>
              <Typography.Text type="secondary">
                Generate your verification reports
              </Typography.Text>
            </div>

            {isDownloading && (
              <Card style={{ marginBottom: '24px', border: '1px solid #1890ff' }}>
                <div style={{ textAlign: 'center', padding: '16px' }}>
                  <Typography.Text strong style={{ color: '#1890ff' }}>
                    Generating Report...
                  </Typography.Text>
                  <Progress 
                    percent={Math.round(downloadProgress)} 
                    status="active"
                    style={{ marginTop: '12px' }}
                  />
                </div>
              </Card>
            )}

            <Card style={{ border: '1px solid #e1e5e9' }}>
              <Space direction="vertical" size="large" style={{ width: "100%" }}>
                <div>
                  1. Download Verification Report (DOCX)
                </div>
                <Button
                  type="primary"
                  size="large"
                  icon={<DownloadOutlined />}
                  onClick={() => setReportTypeModalVisible(true)}
                  disabled={isDownloading}
                  style={{ width: "100%", height: "48px" }}
                >
                  Download
                </Button>

                <div>
                  2. Verify and convert to pdf
                </div>
                <div>
                  3. Upload PDF to get Full report
                </div>
                <CbfReportDownload
                  renderThisInstead={
                    <Button
                      size="large"
                      icon={<FileTextOutlined />}
                      disabled={isDownloading}
                      style={{ width: "100%", height: "48px" }}
                    >
                      Upload PDF
                    </Button>
                  }
                  callFunctionInsteadOfDownload={mergeAndDownloadPDF}
                  year={2024}
                  results={store.getState().app.results}
                />
              </Space>
            </Card>

            <div style={{ textAlign: "center", marginTop: "32px" }}>
              <Button 
                size="large" 
                onClick={() => setCurrentStep(2)}
                icon={<CommentOutlined />}
              >
                Back
              </Button>
            </div>
          </div>
        );
      default:
        return null;
    }
  }, [currentStep, useWtt, year, reviewer, authorizer, consultants, renderReportDataFields, tableDataSource, setTableDataSource, isDownloading, downloadProgress]);

  const ReportTypeModal = useMemo(() => (
    <Modal
      title="Select Report Type"
      open={reportTypeModalVisible}
      onCancel={() => setReportTypeModalVisible(false)}
      footer={null}
      centered
      width={500}
    >
      <div style={{ padding: "16px 0px" }}>
        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
          <Button
            type="primary"
            size="large"
            onClick={() => handleDownloadReport(1)}
            disabled={isDownloading}
            style={{ width: "100%", height: "48px" }}
          >
            Simple Report
          </Button>
          
          <Button
            type="primary"
            size="large"
            onClick={() => handleDownloadReport(2)}
            disabled={isDownloading}
            style={{ width: "100%", height: "48px", backgroundColor: "#722ed1", borderColor: "#722ed1" }}
          >
            MX Report
          </Button>
          
          <Button
            type="primary"
            size="large"
            onClick={() => handleDownloadReport(3)}
            disabled={isDownloading}
            style={{ width: "100%", height: "48px", backgroundColor: "#fa8c16", borderColor: "#fa8c16" }}
          >
            DC Report
          </Button>
        </Space>
        
        <div style={{ textAlign: "center", marginTop: "24px" }}>
          <Button onClick={() => setReportTypeModalVisible(false)}>
            Cancel
          </Button>
        </div>
      </div>
    </Modal>
  ), [reportTypeModalVisible, handleDownloadReport, isDownloading]);

  // Show loading until consultants are loaded
  if (!results || !consultantsLoaded) return <Loading />;

  if (!consultant)
    return (
      <div style={{ 
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f5f5f5'
      }}>
        <Card style={{ maxWidth: "500px", textAlign: "center" }}>
          <div style={{ fontSize: '48px', marginBottom: '24px' }}>🔐</div>
          <Typography.Title level={4} style={{ marginBottom: "16px" }}>
            Authentication Required
          </Typography.Title>
          <Typography.Paragraph style={{ marginBottom: "24px" }}>
            Please log in with your consultant credentials.
          </Typography.Paragraph>
        </Card>
      </div>
    );

  return (
    <div style={{ minHeight: "100vh", backgroundColor: '#f5f5f5' }}>
      <div style={{ margin: "0 auto", padding: '16px' }}>
        <div style={{ marginBottom: "32px", backgroundColor: 'white', borderRadius: '8px', padding: '16px', border: '1px solid #e1e5e9' }}>
          <Steps current={currentStep} items={steps} />
        </div>

        {renderCurrentStep()}
        {ReportTypeModal}

        {/* AutoComplete Modal */}
        <AutoCompleteModal
          key={currentEditingFieldKey}
          visible={autoCompleteModalVisible}
          onCancel={() => setAutoCompleteModalVisible(false)}
          onSave={handleAutoCompleteModalSave}
          title={`Edit ${currentEditingFieldKey.replace(/([A-Z])/g, " $1").replace(/^./, (str) => str.toUpperCase()).replaceAll("_", " ")}`}
          value={reportData[currentEditingFieldKey] || ""}
          suggestions={autocompleteOptions[currentEditingFieldKey] || []}
          placeholder={`Enter ${currentEditingFieldKey.replace(/([A-Z])/g, " $1").toLowerCase()}...`}
          onAddSuggestion={handleAddSuggestionFromAutoCompleteModal}
          onRemoveSuggestion={handleRemoveSuggestionFromAutoCompleteModal}
          fieldKey={currentEditingFieldKey}
        />
      </div>
    </div>
  );
};

export default function VerificationWrapper() {
  const results = useSelector(selectResults);
  const [consultant, setConsultant] = useAtom(consultantAtom);
  const [detailsModalVisible, setDetailsModalVisible] = useState(false);

  if (!results) return <Loading />;

  const consultantMenu = {
    items: [
      {
        key: 'details',
        label: 'Personal Details',
        icon: <UserOutlined />,
        onClick: () => setDetailsModalVisible(true)
      },
      {
        key: 'logout',
        label: 'Logout',
        icon: <LogoutOutlined />,
        onClick: () => {
          if (window.confirm("Confirm Consultant Logout?")) {
            message.success("Logged out, reloading...");
            setConsultant(null);
            localStorage.removeItem("consultant_token");
            window.location.reload();
          }
        }
      }
    ]
  };

  return (
    <div style={{ 
      display: "flex", 
      flexDirection: "column", 
      width: "100%", 
      minHeight: "100vh",
      backgroundColor: '#f5f5f5'
    }}>
      <div style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "16px 24px",
        backgroundColor: 'white',
        borderBottom: '1px solid #e1e5e9',
        marginBottom: "16px"
      }}>
        <div>
          <Typography.Title level={4} style={{ margin: 0, color: '#1f2937' }}>
            Welcome, {consultant?.firstName} {consultant?.lastName}
          </Typography.Title>
          <Typography.Text type="secondary" style={{ fontSize: '14px' }}>
            Verification Dashboard
          </Typography.Text>
        </div>
        <Dropdown menu={consultantMenu} trigger={['click']}>
          <Button icon={<UserOutlined />}>
            Account <DownOutlined />
          </Button>
        </Dropdown>
      </div>

      <Modal
        title="Personal Details"
        open={detailsModalVisible}
        onCancel={() => setDetailsModalVisible(false)}
        footer={[
          <Button 
            key="signature" 
            type="link" 
            onClick={() => downloadFileFromStorage(consultant?.signature, "your_signature.png")}
            icon={<DownloadOutlined />}
          >
            Download Signature
          </Button>,
          <Button key="close" onClick={() => setDetailsModalVisible(false)}>
            Close
          </Button>
        ]}
        width={600}
      >
        <div style={{ marginBottom: "16px" }}>
          <Typography.Text strong>Details:</Typography.Text>
          <pre style={{ 
            marginTop: "8px", 
            whiteSpace: "pre-wrap", 
            padding: "12px", 
            borderRadius: "6px", 
            border: "1px solid #d9d9d9",
            backgroundColor: '#fafafa'
          }}>
            {consultant?.details_for_template}
          </pre>
        </div>
        <div>
          <Typography.Text strong>Summary:</Typography.Text>
          <pre style={{ 
            marginTop: "8px", 
            whiteSpace: "pre-wrap", 
            padding: "12px", 
            borderRadius: "6px", 
            border: "1px solid #d9d9d9",
            backgroundColor: '#fafafa'
          }}>
            {consultant?.summary_for_template}
          </pre>
        </div>
      </Modal>

      <div style={{ flex: 1, padding: "0 16px" }}>
        <Tabs
          defaultActiveKey="3"
          style={{ height: "100%" }}
          type="card"
          size="large"
        >
          <TabPane tab={<span><BuildOutlined /> Functions</span>} key="1">
            <div style={{ backgroundColor: 'white', borderRadius: '8px', minHeight: '400px', padding: '16px', border: '1px solid #e1e5e9' }}>
              <ResultFunctions />
            </div>
          </TabPane>
          <TabPane tab={<span><UserOutlined /> Permissions</span>} key="2">
            <div style={{ backgroundColor: 'white', borderRadius: '8px', minHeight: '400px', padding: '16px', border: '1px solid #e1e5e9' }}>
              <Permissions />
            </div>
          </TabPane>
          <TabPane tab={<span><EditOutlined /> Edit Plan</span>} key="3">
            <div style={{ backgroundColor: 'white', borderRadius: '8px', minHeight: '400px', padding: '16px', border: '1px solid #e1e5e9' }}>
              <Companies is_consultant_page={true} />
            </div>
          </TabPane>
          <TabPane tab={<span><CheckCircleOutlined /> Verification</span>} key="4">
            <div style={{ backgroundColor: 'white', borderRadius: '8px', minHeight: '400px', padding: '16px', border: '1px solid #e1e5e9' }}>
              <Verification />
            </div>
          </TabPane>
          <TabPane tab={<span><SettingOutlined /> Lock Data</span>} key="5">
            <div style={{ backgroundColor: 'white', borderRadius: '8px', minHeight: '400px', padding: '16px', border: '1px solid #e1e5e9' }}>
              <LockData />
            </div>
          </TabPane>
          <TabPane tab={<span><RocketOutlined /> Analysis</span>} key="6">
            <div style={{ backgroundColor: 'white', borderRadius: '8px', minHeight: '400px', padding: '16px', border: '1px solid #e1e5e9' }}>
              <AnalysisConsultant />
            </div>
          </TabPane>
          <TabPane tab={<span><FileTextOutlined /> Results</span>} key="7">
            <div style={{ backgroundColor: 'white', borderRadius: '8px', minHeight: '400px', padding: '16px', border: '1px solid #e1e5e9' }}>
              <AnalysisResult />
            </div>
          </TabPane>
                 </Tabs>
      </div>
    </div>
  );
}

